import React from 'react';
import { first, groupBy } from 'lodash';
import moment from 'moment';
import { List, AutoSizer } from 'react-virtualized';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Divider,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { MinimalItem } from 'src/pages/AssortmentBuild/Planning.types';
import { usePlanQueueStyles } from './PlanQueue.styles';
import styles from './PlanQueue.styles';

const ROW_HEIGHT = 60;

interface AllocationPlanSectionProps {
  items: MinimalItem[];
  componentType?: {
    [key: string]: string;
  };
  sectionName: string;
  secondaryList?: (item?: MinimalItem) => JSX.Element;
  badgeColor: string;
}

function getJobTime(items: MinimalItem[]): string {
  const rawTime = first(items)?.updatedAt ?? '-';
  const time = rawTime !== '-' ? moment(`${rawTime}`).format('h:mma') : rawTime;
  return time;
}

export const AllocationPlanSection = (props: AllocationPlanSectionProps) => {
  const { items, componentType = {}, secondaryList, sectionName, badgeColor } = props;
  const itemsCount = items.length;
  const totalSectionHeight = itemsCount * ROW_HEIGHT;
  const planQueueStyles = usePlanQueueStyles(badgeColor, totalSectionHeight)();
  const groupedItems = groupBy(items, (item) => item.jobId);
  const groups = Object.keys(groupedItems);

  return (
    <React.Fragment>
      <section className={styles.item}>
        <Accordion square className={planQueueStyles.accordionContainer}>
          <div className={styles.itemTitle}>
            <Tooltip title={itemsCount} placement="top-start" arrow>
              <Avatar className={planQueueStyles.small} variant="square">
                <span>{itemsCount > 99 ? '99+' : itemsCount}</span>
              </Avatar>
            </Tooltip>
            <AccordionSummary
              className={planQueueStyles.accordionSummary}
              expandIcon={<i className="fa fa-chevron-down"></i>}
            >
              <span className={planQueueStyles.medium}>{sectionName}</span>
            </AccordionSummary>
          </div>
          <AccordionDetails classes={{ root: styles.accordionRoot }}>
            {/* List's noRowsRenderer is never triggered, manually falling back instead */}
            {itemsCount === 0 ? (
              <ListItem>
                <ListItemText secondary={`No ${sectionName} items`} />
              </ListItem>
            ) : (
              groups.map((group) => {
                const groupItems = groupedItems[group];
                const jobTime = getJobTime(groupItems);
                return (
                  <Accordion key={group} square className={planQueueStyles.accordionContainer}>
                    <div className={styles.itemTitle}>
                      <Tooltip title={itemsCount} placement="top-start" arrow>
                        <Avatar style={{ marginRight: 0 }} className={planQueueStyles.small} variant="square">
                          <span>{itemsCount > 99 ? '99+' : itemsCount}</span>
                        </Avatar>
                      </Tooltip>
                      <AccordionSummary
                        className={planQueueStyles.accordionSummary}
                        expandIcon={<i className="fa fa-chevron-down"></i>}
                      >
                        <Typography variant="subtitle2">Worklist started at {jobTime}</Typography>
                      </AccordionSummary>
                    </div>
                    <AccordionDetails classes={{ root: styles.accordionRoot }}>
                      <div className={planQueueStyles.accordionDetailsList}>
                        <AutoSizer>
                          {({ width }) => (
                            <List
                              width={width}
                              height={totalSectionHeight >= 500 ? 500 : totalSectionHeight}
                              overscanRowCount={10}
                              rowCount={items.length}
                              rowHeight={ROW_HEIGHT}
                              rowRenderer={({ index, key, style }) => {
                                const item = items[index];
                                const primaryText = item.primaryText;
                                return (
                                  <div key={key} style={style}>
                                    <Tooltip title={primaryText} placement="top-start">
                                      <ListItem>
                                        <ListItemText
                                          primary={primaryText}
                                          secondary={secondaryList ? secondaryList(item) : null}
                                          primaryTypographyProps={{
                                            gutterBottom: true,
                                            variant: 'subtitle2',
                                            noWrap: true,
                                          }}
                                          secondaryTypographyProps={componentType}
                                        />
                                      </ListItem>
                                    </Tooltip>
                                  </div>
                                );
                              }}
                            />
                          )}
                        </AutoSizer>
                      </div>
                    </AccordionDetails>
                  </Accordion>
                );
              })
            )}
          </AccordionDetails>
        </Accordion>
        <Divider />
      </section>
    </React.Fragment>
  );
};
